import { Injectable, NgZone, signal, computed, inject } from '@angular/core';
import { environment } from '../../../environments/environment';

declare const google: any;

export interface GoogleUser {
  name: string;
  email: string;
  picture: string;
}

interface StoredAuth {
  user: GoogleUser;
  credential: string;
  exp: number;
}

const STORAGE_KEY = 'ours_auth';

@Injectable({ providedIn: 'root' })
export class GoogleAuthService {
  private _user       = signal<GoogleUser | null>(null);
  private _credential = signal<string | null>(null);
  private ngZone      = inject(NgZone);
  private initialized = false;
  private refreshTimer: ReturnType<typeof setTimeout> | null = null;

  readonly user       = this._user.asReadonly();
  readonly credential = this._credential.asReadonly();
  readonly isSignedIn = computed(() => this._user() !== null);

  constructor() {
    // Restauration immédiate — permet au guard de passer sans attendre Google
    this.restoreFromStorage();
  }

  init(): void {
    if (typeof google === 'undefined' || !google?.accounts?.id) return;
    if (this.initialized) return;
    this.initialized = true;

    // Exécuter hors zone Angular pour éviter les violations setTimeout/rAF
    this.ngZone.runOutsideAngular(() => {
      google.accounts.id.initialize({
        client_id:            environment.googleClientId,
        auto_select:          true,
        use_fedcm_for_prompt: false,
        callback:             (response: any) => {
          try {
            const cred    = response.credential;
            const b64     = cred.split('.')[1].replace(/-/g, '+').replace(/_/g, '/');
            const json    = decodeURIComponent(atob(b64).split('').map(c => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2)).join(''));
            const payload = JSON.parse(json);
            const user: GoogleUser = { name: payload.name, email: payload.email, picture: payload.picture };
            this.ngZone.run(() => {
              this._user.set(user);
              this._credential.set(cred);
              this.persist(user, cred, payload.exp);
              this.scheduleRefresh(payload.exp);
            });
          } catch {
            console.error('Google Auth: failed to decode credential');
          }
        },
      });

      if (!this._user()) {
        google.accounts.id.prompt((notification: any) => {
          if (notification.isNotDisplayed() || notification.isSkippedMoment()) return;
        });
      }
    });
  }

  renderButton(element: HTMLElement): void {
    if (typeof google === 'undefined' || !google?.accounts?.id) return;
    google.accounts.id.renderButton(element, {
      type:   'standard',
      theme:  'outline',
      size:   'large',
      locale: 'fr',
    });
  }

  signOut(): void {
    if (this.refreshTimer) {
      clearTimeout(this.refreshTimer);
      this.refreshTimer = null;
    }
    if (typeof google !== 'undefined' && google?.accounts?.id) {
      google.accounts.id.disableAutoSelect();
    }
    this._user.set(null);
    this._credential.set(null);
    localStorage.removeItem(STORAGE_KEY);
  }

  private scheduleRefresh(exp: number): void {
    if (this.refreshTimer) clearTimeout(this.refreshTimer);
    // Rafraîchir 5 minutes avant expiration
    const msUntilRefresh = (exp - 5 * 60) * 1000 - Date.now();
    if (msUntilRefresh <= 0) {
      google.accounts.id.prompt();
      return;
    }
    this.refreshTimer = setTimeout(() => {
      if (typeof google !== 'undefined' && google?.accounts?.id) {
        google.accounts.id.prompt((notification: any) => {
          if (notification.isNotDisplayed() || notification.isSkippedMoment()) return;
        });
      }
    }, msUntilRefresh);
  }

  private restoreFromStorage(): void {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (!raw) return;
      const stored: StoredAuth = JSON.parse(raw);
      if (!stored?.exp || Date.now() / 1000 >= stored.exp) {
        localStorage.removeItem(STORAGE_KEY);
        return;
      }
      this._user.set(stored.user);
      this._credential.set(stored.credential);
    } catch {
      localStorage.removeItem(STORAGE_KEY);
    }
  }

  private persist(user: GoogleUser, credential: string, exp: number): void {
    localStorage.setItem(STORAGE_KEY, JSON.stringify({ user, credential, exp }));
  }
}
